import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { IGetCochesClientsResponse, IGetUserGroupsResponse } from './userGroupsSaga';
import { ModuleType, UserPermissionType } from '../../types/commonTypes';

export interface GroupMember {
  id: string;
  firstName: string;
  lastName: string;
  profilePic: string;
  userGroupId?: string;
}
export interface IUserPermission {
  id: string;
  permissionCode: UserPermissionType;
  description: string;
}
export interface IModule {
  id: string;
  name: ModuleType;
  description: string;
  UserPermissions: IUserPermission[];
}
export interface IUserGroup {
  id: string;
  name: string;
  coaches: GroupMember[];
  clients: GroupMember[];
  modules: IModule[];
}
export interface ICreateUserGroupPayload {
  id?: string;
  name: string;
  userPermissionIds: string[];
}

export interface IAddRemoveMembersPayload {
  id: string;
  coachIds: string[];
  clientIds: string[];
}
interface IUserGroupsState {
  userGroups: IUserGroup[];
  modules: IModule[];
  totalUserGroupsCount: number;
  clients: GroupMember[];
  coaches: GroupMember[];
  createdUserGroup: { id: string; name: string };
  fetchUsergroupsLoading: boolean;
  deleteUserGroupLoading: boolean;
  fetchModulesLoading: boolean;
  createUserGroupSuccess: boolean;
  createUserGroupLoading: boolean;
  deleteUserGroupSuccess: boolean;
  updateUserGroupSuccess: boolean;
  fetchCoachesAndClientsLoading: boolean;
  addRemoveMembersLoading: boolean;
  addRemoveMembersSuccess: boolean;
  error: { hasError: boolean; message: string };
}

const initialState: IUserGroupsState = {
  userGroups: [],
  totalUserGroupsCount: 0,
  modules: [],
  fetchUsergroupsLoading: false,
  deleteUserGroupLoading: false,
  deleteUserGroupSuccess: false,
  fetchModulesLoading: false,
  error: {
    hasError: false,
    message: '',
  },
  createUserGroupSuccess: false,
  createUserGroupLoading: false,
  updateUserGroupSuccess: false,
  fetchCoachesAndClientsLoading: false,
  clients: [],
  coaches: [],
  addRemoveMembersLoading: false,
  addRemoveMembersSuccess: false,
  createdUserGroup: {
    id: '',
    name: '',
  },
};

const userGroupsSlice = createSlice({
  name: 'userGroups',
  initialState,
  reducers: {
    fetchAllUserGroups(
      state,
      _action: PayloadAction<{
        skip: number;
        take: number;
        searchQuery: string;
        organizationId: string;
      }>
    ) {
      state.fetchUsergroupsLoading = true;
    },
    fetchAllUserGroupsSuccess(state, action: PayloadAction<IGetUserGroupsResponse>) {
      state.fetchUsergroupsLoading = false;
      state.userGroups = action.payload.getUserGroups.groupList;
      state.totalUserGroupsCount = action.payload.getUserGroups.totalClientCount;
    },
    fetchAllUserGroupsFailure(state, action: PayloadAction<string>) {
      state.fetchUsergroupsLoading = false;
      state.error = { hasError: true, message: action.payload };
    },
    deleteUserGroup(state, _action: PayloadAction<string>) {
      state.deleteUserGroupLoading = true;
    },
    deleteUserGroupSuccess(state) {
      state.deleteUserGroupLoading = false;
      state.deleteUserGroupSuccess = true;
    },
    deleteUserGroupFailure(state, action: PayloadAction<string>) {
      state.deleteUserGroupLoading = false;
      state.error = { hasError: true, message: action.payload };
    },
    resetDeleteUserGroupSuccess(state) {
      state.deleteUserGroupSuccess = false;
    },
    fetchAllModules(state) {
      state.fetchModulesLoading = true;
    },
    fetchAllModulesSuccess(state, action: PayloadAction<IModule[]>) {
      state.fetchModulesLoading = false;
      state.modules = action.payload;
    },
    fetchAllModulesFailure(state, action: PayloadAction<string>) {
      state.fetchModulesLoading = false;
      state.error = { hasError: true, message: action.payload };
    },
    createUpdateUserGroup(
      state,
      _action: PayloadAction<{ values: ICreateUserGroupPayload; isCreate: boolean }>
    ) {
      state.createUserGroupLoading = true;
    },
    createUserGroupSuccess(state, action: PayloadAction<{ id: string; name: string }>) {
      state.createUserGroupLoading = false;
      state.createUserGroupSuccess = true;
      state.createdUserGroup = action.payload;
    },
    updateUserGroupSuccess(state) {
      state.createUserGroupLoading = false;
      state.updateUserGroupSuccess = true;
    },
    createUpdateUserGroupFailure(state, action: PayloadAction<string>) {
      state.createUserGroupLoading = false;
      state.error = { hasError: true, message: action.payload };
    },
    resetCreateUserGroupSuccess(state) {
      state.createUserGroupSuccess = false;
    },
    resetUpdateUserGroupSuccess(state) {
      state.updateUserGroupSuccess = false;
    },
    fetchCoachesAndClientsRequest(
      state,
      _action: PayloadAction<{ organizationId: string; filterText: string }>
    ) {
      state.fetchCoachesAndClientsLoading = true;
    },
    fetchCoachesAndClientsRequestSuccess(state, action: PayloadAction<IGetCochesClientsResponse>) {
      state.fetchCoachesAndClientsLoading = false;
      state.clients = action.payload.getCoachedWithClients.clients;
      state.coaches = action.payload.getCoachedWithClients.coaches;
    },
    fetchCoachesAndClientsRequestFailure(state, action: PayloadAction<string>) {
      state.fetchCoachesAndClientsLoading = false;
      state.error = { hasError: true, message: action.payload };
    },
    addRemoveMembers(state, _action: PayloadAction<IAddRemoveMembersPayload>) {
      state.addRemoveMembersLoading = true;
    },
    addRemoveMembersSuccess(state) {
      state.addRemoveMembersLoading = false;
      state.addRemoveMembersSuccess = true;
    },
    addRemoveMembersFailure(state, action: PayloadAction<string>) {
      state.addRemoveMembersLoading = false;
      state.error = { hasError: true, message: action.payload };
    },
    resetAddRemoveMembersSuccess(state) {
      state.addRemoveMembersSuccess = false;
    },
  },
});

const userGroupsActions = userGroupsSlice.actions;
const userGroupsReducer = userGroupsSlice.reducer;

export { userGroupsActions, userGroupsReducer };
